import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { Calendar, CalendarProps } from "../Calendar/Calendar";
import { CalendarController } from "../CalendarController";
import { CalendarLabel } from "../CalendarLabel";
import { CalendarUIContainer } from "./CalendarUIContainer";
import { CalendarUIHeader } from "./CalendarUIHeader";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

const normalize = (config: dayjs.ConfigType) => {
  return dayjs(dayjs(config).format("YYYY-MM-DD"));
};

export const useCalendar = (
  {
    initialDate: config = dayjs(),
    min,
    max,
  }: {
    initialDate?: dayjs.ConfigType;
    min?: dayjs.ConfigType;
    max?: dayjs.ConfigType;
  } = { initialDate: undefined, min: undefined, max: undefined },
) => {
  const [date, setDate] = useState(normalize(config));

  const increment = useCallback(() => {
    setDate((prev) => prev.add(1, "month"));
  }, []);
  const decrement = useCallback(() => {
    setDate((prev) => prev.add(-1, "month"));
  }, []);

  const reset = useCallback(() => {
    setDate(dayjs(dayjs(config).format("YYYY-MM-DD")));
  }, [config]);
  const disabledPrev = useMemo(
    () =>
      min !== null &&
      min !== undefined &&
      dayjs(min).isSameOrAfter(date.add(-1, "month").endOf("month")),
    [date, min],
  );
  const disabledNext = useMemo(
    () =>
      max !== null &&
      max !== undefined &&
      dayjs(max).isSameOrBefore(date.add(1, "month").startOf("month")),
    [date, max],
  );

  const set = useCallback((d: dayjs.ConfigType = new Date()) => {
    setDate(normalize(d));
  }, []);

  return {
    date,
    increment,
    decrement,
    reset,
    disabledNext,
    disabledPrev,
    set,
    controllerProps: {
      unit: "month",
      set,
      increment,
      decrement,
      disabledNext,
      disabledPrev,
    } as const,
  };
};

export interface CalendarUIProps<T> extends CalendarProps<T> {
  /**
   * 元々かなり大きいコンポーネントのため、`"large"`サイズはサポートしません。
   * @default "medium"
   */
  size?: "small" | "medium";
}

export const CalendarUI = forwardRef(
  <T,>(
    {
      events,
      dates,
      date: propsDate,
      min,
      max,
      daysProps,
      cellHeight,
      size = "medium",
      renderCell,
      onClickDay,
      ...props
    }: CalendarUIProps<T>,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const { date, increment, decrement, set } = useCalendar({
      initialDate: propsDate,
      min,
      max,
    });

    useEffect(() => {
      if (propsDate !== null && propsDate !== undefined) {
        set(propsDate);
      }
    }, [propsDate, set]);

    return (
      <CalendarUIContainer {...props} ref={ref} size={size}>
        <CalendarUIHeader>
          <CalendarController
            unit="month"
            size={size}
            onPrev={decrement}
            onNext={increment}
            onReset={set}
          />

          <CalendarLabel date={date} size={size} />
        </CalendarUIHeader>
        <Calendar
          renderCell={renderCell}
          events={events}
          dates={dates}
          daysProps={daysProps}
          date={date}
          min={min}
          max={max}
          cellHeight={cellHeight}
          onClickDay={onClickDay}
        />
      </CalendarUIContainer>
    );
  },
) as <Props>(
  props: CalendarUIProps<Props> & {
    ref?: React.Ref<HTMLDivElement>;
  },
) => React.JSX.Element;
